import { Link } from "react-router-dom";
import { Phone, Mail, ArrowRight, ShieldCheck, KeyRound, HandCoins } from "lucide-react";
import { SITE, FAQS } from "@/data/site";
import useProperties from "@/hooks/useProperties";
import PropertyCard from "@/components/PropertyCard";
import EnquiryForm from "@/components/EnquiryForm";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import HomeHero from "@/components/HomeHero";
import DestinationSlider from "@/components/DestinationSlider";
import { HERO_SLIDES } from "@/data/homeMedia";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const HOME_JSONLD = {
  "@context": "https://schema.org",
  "@type": "LodgingBusiness",
  name: SITE.name,
  description: SITE.tagline,
  telephone: SITE.phone,
  email: SITE.email,
  areaServed: "South East Queensland, Australia",
  url: `https://${SITE.domain}`,
};

export default function Home() {
  const properties = useProperties();

  return (
    <main data-testid="home-page">
      <Seo
        title="PARADISIO RESORTS | Kirra Beach &amp; Hervey Bay Accommodation — Book Direct"
        description="Four privately owned Queensland holiday homes: absolute beachfront Kirra Beach accommodation at Coolangatta, plus a one-bedroom, a studio and an ocean-view apartment at Urangan, Hervey Bay. Book direct with the owner and save."
        image={HERO_SLIDES[0].src}
        imageAlt={HERO_SLIDES[0].alt}
        jsonLd={HOME_JSONLD}
      />

      <HomeHero />

      {/* Properties */}
      <section id="properties" className="max-w-[1400px] mx-auto px-6 md:px-10 mt-20 md:mt-28 scroll-mt-24">
        <Reveal className="max-w-2xl">
          <p className="text-[11px] uppercase tracking-[0.32em] text-[#B08A3E]">The properties</p>
          <h2 className="font-display text-4xl sm:text-5xl text-[#0F2A52] mt-5 gold-rule">
            Two coasts, four places to stay
          </h2>
          <p className="text-base text-[#4A4A4A] leading-relaxed mt-10">
            Kirra, on the southern Gold Coast, is the quiet end of the strip: a world-famous stretch of surf
            with cafés, restaurants, bars and surf clubs a short walk up the street, Coolangatta's shops and
            markets close by, and Gold Coast Airport only minutes away. Urangan, in Hervey Bay, is the marina
            town whales pass each winter and the gateway across to K'gari (Fraser Island).
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 mt-14">
          {properties.map((p, i) => (
            <PropertyCard key={p.slug} property={p} index={i} />
          ))}
        </div>
      </section>

      <DestinationSlider />

      {/* Trust strip */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 mt-24 md:mt-32">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12 border-t border-[#0F2A52]/10 pt-12">
          {[
            { icon: KeyRound, t: "Locally managed", d: "Each property has its own local manager on the ground for keys, cleaning and anything you need during your stay." },
            { icon: HandCoins, t: "10% better, direct", d: "Enquire for your dates and Robert quotes you the total — around 10% under the platform price, because there is no platform." },
            { icon: ShieldCheck, t: "Simple, transparent pricing", d: "No confusing fees and no surprises at checkout. We quote you one clear, fair total for your dates before you pay anything." },
          ].map(({ icon: Icon, t, d }, i) => (
            <Reveal key={t} delay={i * 120}>
              <Icon size={22} className="text-[#B08A3E]" />
              <h3 className="font-display text-2xl mt-5 text-[#0F2A52]">{t}</h3>
              <p className="text-sm text-[#4A4A4A] leading-relaxed mt-3">{d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* About the owner */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 mt-24 md:mt-32">
        <Reveal className="bg-[#F2EBDD] p-8 md:p-14 border-l-2 border-[#B08A3E]">
          <div className="max-w-3xl">
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#B08A3E]">Your host</p>
            <h2 className="font-display text-4xl sm:text-5xl text-[#0F2A52] mt-5">
              Owned and managed by Robert
            </h2>
            <p className="text-base text-[#4A4A4A] leading-relaxed mt-6">
              Robert has been welcoming guests to the Queensland coast for years. He owns each of the Paradisio
              homes and handles your booking himself, with a local manager at every property looking after keys,
              cleaning and anything you need once you arrive.
            </p>
            <p className="text-base text-[#4A4A4A] leading-relaxed mt-4">
              If you'd rather talk it through than fill in a form, call him. He'd prefer that.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <a
                href={`tel:${SITE.phone}`}
                className="inline-flex items-center gap-3 bg-[#0F2A52] text-[#FCFAF6] px-7 py-4 text-sm hover:-translate-y-1 hover:shadow-lg transition-transform duration-200"
                data-testid="about-call-btn"
              >
                <Phone size={16} /> {SITE.phoneDisplay}
              </a>
              <a
                href={`mailto:${SITE.email}`}
                className="inline-flex items-center gap-3 border border-[#0F2A52]/30 px-7 py-4 text-sm text-[#0F2A52] hover:bg-[#FCFAF6] transition-colors duration-200"
                data-testid="about-email-btn"
              >
                <Mail size={16} /> Email us
              </a>
            </div>
          </div>
        </Reveal>
      </section>

      {/* FAQ + enquiry */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 mt-24 md:mt-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="min-w-0 lg:col-span-7">
            <Reveal>
              <p className="text-[11px] uppercase tracking-[0.32em] text-[#B08A3E]">Good to know</p>
              <h2 className="font-display text-4xl sm:text-5xl text-[#0F2A52] mt-5 mb-14 gold-rule">
                How booking direct works
              </h2>
            </Reveal>
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`} className="border-b border-[#0F2A52]/15">
                  <AccordionTrigger className="text-left font-display text-xl md:text-2xl text-[#0F2A52] py-6 hover:no-underline hover:text-[#B08A3E] transition-colors duration-200">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-[#4A4A4A] leading-relaxed pb-6 pr-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          <div className="min-w-0 lg:col-span-5" id="enquire">
            <Reveal delay={120} className="glass-card p-8 md:p-10">
              <h2 className="font-display text-3xl text-[#0F2A52]">Make an enquiry</h2>
              <p className="text-sm text-[#4A4A4A] leading-relaxed mt-3 mb-8">
                Tell Robert your dates and he'll come back to you with availability and a direct rate.
              </p>
              <EnquiryForm />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 mt-24">
        <div className="border-t border-[#0F2A52]/10 pt-10">
          <Link
            to="/enquire"
            className="font-display text-3xl md:text-4xl text-[#0F2A52] inline-flex items-center gap-4 hover:gap-7 hover:text-[#B08A3E] transition-all duration-300"
            data-testid="home-contact-cta"
          >
            Contact PARADISIO RESORTS <ArrowRight size={26} className="text-[#B08A3E]" />
          </Link>
        </div>
      </section>
    </main>
  );
}
